import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { CLINIC_PRIMARY_PHONE } from "@/lib/clinic";

const NAV = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/#about" },
  { label: "Services", href: "/#services" },
  { label: "Gallery", href: "/#gallery" },
  { label: "Reviews", href: "/#testimonials" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const onHome = location.pathname === "/";
    if (href === "/") {
      if (onHome) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
      }
      return;
    }
    // hash links like /#about
    const hashIdx = href.indexOf("#");
    const id = hashIdx >= 0 ? href.slice(hashIdx + 1) : "";
    if (onHome && id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(href);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/70 border-b border-clinic-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3" onClick={(e) => handleNav(e, "/")}>
          <div className="w-10 h-10 rounded-full bg-clinic-pine text-clinic-bg grid place-items-center font-heading text-lg font-semibold">
            A
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[17px] font-semibold text-clinic-ink">The Dental Clinic</div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-clinic-muted">by Dr. Ashish Kumar</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              onClick={(e) => handleNav(e, n.href)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-clinic-ink/80 hover:text-clinic-pine transition-colors cursor-pointer"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CLINIC_PRIMARY_PHONE.tel}`}
            data-testid="call-now-btn"
            className="text-sm text-clinic-ink/80 hover:text-clinic-pine inline-flex items-center gap-2"
          >
            <Phone size={16} /> {CLINIC_PRIMARY_PHONE.display}
          </a>
          <Link to="/book" data-testid="header-book-btn" className="btn-primary text-sm">
            Book Appointment
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-full border border-clinic-line bg-white/70"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-clinic-line">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={(e) => handleNav(e, n.href)}
                className="text-base text-clinic-ink/85 cursor-pointer"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
            <Link to="/book" data-testid="mobile-book-btn" className="btn-primary w-full justify-center mt-2">
              Book Appointment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
